import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReportBuilder = ({ isOpen, onClose, onSave }) => {
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    metrics: [],
    dimensions: [],
    filters: {},
    schedule: 'manual',
    format: 'pdf',
    recipients: []
  });

  const availableMetrics = [
    { value: 'total_revenue', label: 'Total Revenue', category: 'Revenue' },
    { value: 'erpc', label: 'eRPC (Revenue Per Call)', category: 'Revenue' },
    { value: 'call_volume', label: 'Call Volume', category: 'Volume' },
    { value: 'win_rate', label: 'Win Rate', category: 'Performance' },
    { value: 'conversion_rate', label: 'Conversion Rate', category: 'Performance' },
    { value: 'avg_bid_amount', label: 'Average Bid Amount', category: 'Bidding' },
    { value: 'publisher_payout', label: 'Publisher Payout', category: 'Revenue' },
    { value: 'profit_margin', label: 'Profit Margin', category: 'Revenue' },
    { value: 'call_duration', label: 'Average Call Duration', category: 'Quality' },
    { value: 'lead_quality_score', label: 'Lead Quality Score', category: 'Quality' }
  ];

  const availableDimensions = [
    { value: 'date', label: 'Date' },
    { value: 'traffic_source', label: 'Traffic Source' },
    { value: 'geography', label: 'Geography' },
    { value: 'buyer_segment', label: 'Buyer Segment' },
    { value: 'publisher', label: 'Publisher' },
    { value: 'campaign', label: 'Campaign' },
    { value: 'time_of_day', label: 'Time of Day' },
    { value: 'day_of_week', label: 'Day of Week' }
  ];

  const scheduleOptions = [
    { value: 'manual', label: 'Manual (On-demand)' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Report' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV Data' },
    { value: 'dashboard', label: 'Interactive Dashboard' }
  ];

  const savedTemplates = [
    {
      id: 'executive_summary',
      name: 'Executive Summary',
      description: 'High-level KPIs and trends for leadership',
      metrics: ['total_revenue', 'erpc', 'call_volume', 'win_rate'],
      dimensions: ['date', 'traffic_source']
    },
    {
      id: 'publisher_performance',
      name: 'Publisher Performance',
      description: 'Detailed publisher metrics and payouts',
      metrics: ['total_revenue', 'call_volume', 'publisher_payout', 'conversion_rate'],
      dimensions: ['publisher', 'date', 'traffic_source']
    },
    {
      id: 'buyer_analysis',
      name: 'Buyer Analysis',
      description: 'Buyer activity and bidding patterns',
      metrics: ['avg_bid_amount', 'win_rate', 'call_volume', 'conversion_rate'],
      dimensions: ['buyer_segment', 'geography', 'date']
    }
  ];

  const handleMetricToggle = (metricValue) => {
    setReportConfig(prev => ({
      ...prev,
      metrics: prev?.metrics?.includes(metricValue)
        ? prev?.metrics?.filter(m => m !== metricValue)
        : [...prev?.metrics, metricValue]
    }));
  };

  const handleDimensionToggle = (dimensionValue) => {
    setReportConfig(prev => ({
      ...prev,
      dimensions: prev?.dimensions?.includes(dimensionValue)
        ? prev?.dimensions?.filter(d => d !== dimensionValue)
        : [...prev?.dimensions, dimensionValue]
    }));
  };

  const loadTemplate = (template) => {
    setReportConfig(prev => ({
      ...prev,
      name: template?.name,
      description: template?.description,
      metrics: template?.metrics,
      dimensions: template?.dimensions
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(reportConfig);
    }
    onClose();
  };

  const groupedMetrics = availableMetrics?.reduce((acc, metric) => {
    if (!acc?.[metric?.category]) {
      acc[metric.category] = [];
    }
    acc?.[metric?.category]?.push(metric);
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1100 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Custom Report Builder</h2>
              <p className="text-sm text-muted-foreground">Create and schedule custom analytics reports</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Templates Sidebar */}
          <div className="w-80 border-r border-border p-4 overflow-y-auto">
            <h3 className="font-medium text-foreground mb-4">Quick Templates</h3>
            <div className="space-y-2">
              {savedTemplates?.map((template) => (
                <div
                  key={template?.id}
                  className="p-3 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => loadTemplate(template)}
                >
                  <h4 className="font-medium text-foreground text-sm">{template?.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{template?.description}</p>
                  <div className="flex items-center space-x-1 mt-2">
                    <Icon name="BarChart3" size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {template?.metrics?.length} metrics, {template?.dimensions?.length} dimensions
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Report Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Report Name"
                    placeholder="Enter report name"
                    value={reportConfig?.name}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, name: e?.target?.value }))}
                    required
                  />
                  <Input
                    label="Description"
                    placeholder="Brief description"
                    value={reportConfig?.description}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, description: e?.target?.value }))}
                  />
                </div>
              </div>

              {/* Metrics Selection */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Select Metrics</h3>
                {Object.entries(groupedMetrics)?.map(([category, metrics]) => (
                  <div key={category} className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">{category}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {metrics?.map((metric) => (
                        <Checkbox
                          key={metric?.value}
                          label={metric?.label}
                          checked={reportConfig?.metrics?.includes(metric?.value)}
                          onChange={() => handleMetricToggle(metric?.value)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Dimensions Selection */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Group By (Dimensions)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {availableDimensions?.map((dimension) => (
                    <Checkbox
                      key={dimension?.value}
                      label={dimension?.label}
                      checked={reportConfig?.dimensions?.includes(dimension?.value)}
                      onChange={() => handleDimensionToggle(dimension?.value)}
                    />
                  ))}
                </div>
              </div>

              {/* Schedule & Format */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Delivery Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Schedule"
                    options={scheduleOptions}
                    value={reportConfig?.schedule}
                    onChange={(value) => setReportConfig(prev => ({ ...prev, schedule: value }))}
                  />
                  <Select
                    label="Format"
                    options={formatOptions}
                    value={reportConfig?.format}
                    onChange={(value) => setReportConfig(prev => ({ ...prev, format: value }))}
                  />
                </div>
              </div>

              {/* Recipients */}
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">Email Recipients</h3>
                <Input
                  label="Email Addresses"
                  placeholder="Enter email addresses separated by commas"
                  value={reportConfig?.recipients?.join(', ')}
                  onChange={(e) => setReportConfig(prev => ({ 
                    ...prev, 
                    recipients: e?.target?.value?.split(',')?.map(email => email?.trim())?.filter(Boolean)
                  }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {reportConfig?.metrics?.length} metrics, {reportConfig?.dimensions?.length} dimensions selected
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!reportConfig?.name || reportConfig?.metrics?.length === 0}
            >
              Save Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;